import type { TypedSupabaseClient } from '../client';
import type {
  IStorePaymentProviderRepository,
  StorePaymentProvider,
  UpsertStorePaymentProviderInput,
} from '../../../interfaces/src';

const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);

/**
 * Per-tenant payment gateway credentials (store_payment_providers).
 *
 * Follows the delivery_providers security model: tokens are WRITE-ONLY from
 * the dashboard — list() never selects them; the UI only learns whether one
 * is stored. They are read exclusively by the service-role edge functions
 * (storefront-flouci-pay / -verify / -webhook). The storefront (anon) can
 * read just (code, is_enabled) through a column-scoped grant.
 */
export class SupabaseStorePaymentProvidersRepository implements IStorePaymentProviderRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<StorePaymentProvider[]> {
    const { data, error } = await anyFrom(this.supabase)('store_payment_providers')
      .select(PROVIDER_COLUMNS)
      .order('code', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapProvider);
  }

  async upsert(input: UpsertStorePaymentProviderInput): Promise<StorePaymentProvider> {
    const { data: existing, error: getErr } = await anyFrom(this.supabase)('store_payment_providers')
      .select('id')
      .eq('code', input.code)
      .maybeSingle();
    if (getErr) throw getErr;

    const patch: Record<string, unknown> = {};
    if (input.isEnabled !== undefined) patch['is_enabled'] = input.isEnabled;
    // Tokens: undefined = keep stored value; a string (even '') replaces it.
    if (input.publicToken !== undefined) patch['public_token'] = input.publicToken.trim() || null;
    if (input.privateToken !== undefined) patch['private_token'] = input.privateToken.trim() || null;

    if (existing?.id) {
      const { data, error } = await anyFrom(this.supabase)('store_payment_providers')
        .update(patch)
        .eq('id', existing.id)
        .select(PROVIDER_COLUMNS)
        .single();
      if (error) throw error;
      return mapProvider(data);
    }

    // tenant_id is filled by the set_tenant_id_from_session() trigger.
    const { data, error } = await anyFrom(this.supabase)('store_payment_providers')
      .insert({ code: input.code, ...patch })
      .select(PROVIDER_COLUMNS)
      .single();
    if (error) throw error;
    return mapProvider(data);
  }

  async listEnabledCodes(tenantId: string): Promise<string[]> {
    // Anon path — only the column-granted fields, never the tokens.
    const { data, error } = await anyFrom(this.supabase)('store_payment_providers')
      .select('code, is_enabled')
      .eq('tenant_id', tenantId)
      .eq('is_enabled', true);
    if (error) throw error;
    return ((data ?? []) as Array<{ code: string }>).map((row) => row.code);
  }
}

// SECURITY: select the token columns only to learn whether they're set —
// mapProvider reduces them to booleans and the values never leave this file.
const PROVIDER_COLUMNS = 'id, code, is_enabled, public_token, private_token, created_at, updated_at';

function mapProvider(row: Record<string, unknown>): StorePaymentProvider {
  return {
    id: row['id'] as string,
    code: row['code'] as StorePaymentProvider['code'],
    isEnabled: Boolean(row['is_enabled']),
    publicTokenSet: Boolean(row['public_token']),
    privateTokenSet: Boolean(row['private_token']),
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
